<script setup lang="ts">
	import axios from '@/lib/axios'
	import { useQuery } from '@tanstack/vue-query'
	import { computed, onMounted, reactive, ref, watch } from 'vue'
	import { useToast } from 'vue-toastification'

	import AlertComponent from '@/components/AlertComponent.vue'
	import LoadingModal from '@/components/LoadingModal.vue'
	import StyledButton from '@/components/StyledButton.vue'
	import StyledCombobox from '@/components/StyledCombobox.vue'
	import StyledInput from '@/components/StyledInput.vue'
	import StyledSelect from '@/components/StyledSelect.vue'

	import useCountryCodes from '@/composables/useCountryCodes'
	import useEmail from '@/composables/useEmail'
	import useHasPermissionUser from '@/composables/useHasPermissionUser'
	import usePhoneRequest from '@/composables/usePhoneRequest'

	import { EmailTypes } from '@/enums/emailTypes'
	import { PhoneTypes } from '@/enums/phoneTypes'

	import { CountryCode } from '@/interfaces/countryCode'
	import { Email } from '@/interfaces/email'
	import { Phone } from '@/interfaces/phone'

	import useUserEmail from '@/modules/users/composables/useUserEmail'
	import useUserPhone from '@/modules/users/composables/useUserPhone'
	import { User } from '@/modules/users/interfaces/user'

	interface ContactMethod {
		id: string
		type: 'email' | 'phone'
		value: string
		label: string
		originalData?: Email | Phone
		phoneType?: PhoneTypes
		emailType?: EmailTypes
		countryCodeId?: number
		extension?: string
	}

	interface Props {
		user: User
	}

	interface Emits {
		(e: 'update', contactMethods: ContactMethod[]): void
		(e: 'save', contactMethods: ContactMethod[]): void
		(e: 'refresh'): void
	}

	const props = defineProps<Props>()
	const emits = defineEmits<Emits>()
	const toast = useToast()

	// Composables
	const { countryCodes } = useCountryCodes()

	interface FormCountryCode {
		id: number
		value: CountryCode
	}

	const countrySelected = ref<FormCountryCode | undefined>()
	const editCountrySelected = ref<FormCountryCode | undefined>()

	// Fetch user emails using React Query directly
	const {
		data: userEmails,
		isLoading: loadingEmails,
		refetch: refetchEmails,
	} = useQuery(
		['userEmails', props.user.id],
		async () => {
			const { data } = await axios.get<Email[]>(
				`/api/users/${props.user.id}/emails`
			)
			return data
		},
		{
			staleTime: 0, // Siempre refrescar
			cacheTime: 0, // No usar cache
		}
	)

	// Fetch user phones using React Query directly
	const {
		data: usersPhones,
		isLoading: loadingPhones,
		refetch: refetchPhones,
	} = useQuery(
		['userPhones', props.user.id],
		async () => {
			const { data } = await axios.get<Phone[]>(
				`/api/users/${props.user.id}/phones`
			)
			return data
		},
		{
			staleTime: 0, // Siempre refrescar
			cacheTime: 0, // No usar cache
		}
	)

	// CRUD operations
	const { createEmailAsync } = useUserEmail({ immediate: false })
	const { updateEmailAsync, deleteEmailAsync } = useEmail({ immediate: false })
	const { createPhoneAsync } = useUserPhone({ immediate: false })
	const { updatePhoneAsync, deletePhoneAsync } = usePhoneRequest({
		immediate: false,
	})

	// Estado reactivo
	const contactMethods = ref<ContactMethod[]>([])
	const isLoading = computed(() => loadingEmails.value || loadingPhones.value)

	const newContactMethod = reactive<{
		type: 'email' | 'phone'
		value: string
		phoneType: PhoneTypes
		emailType: EmailTypes
		countryCodeId: number | undefined
		extension: string
	}>({
		type: 'email',
		value: '',
		phoneType: PhoneTypes.Mobile,
		emailType: EmailTypes.Personal,
		countryCodeId: 1,
		extension: '',
	})

	const showAddForm = ref(false)
	const deleteConfirm = ref(false)
	const methodToDelete = ref<ContactMethod | null>(null)
	const editingMethod = ref<ContactMethod | null>(null)
	const showEditForm = ref(false)

	// Transform API data to ContactMethod format
	const transformDataToContactMethods = async () => {
		const methods: ContactMethod[] = []

		const emailsToUse = userEmails.value || []
		const phonesToUse = usersPhones.value || []

		// Transform emails
		if (emailsToUse.length > 0) {
			await Promise.all(
				emailsToUse.map(async (email: Email) => {
					methods.push({
						id: `email-${email.id}`,
						type: 'email',
						value: email.email,
						label: 'Personal',
						originalData: email,
						emailType: email.type,
					})
				})
			)
		}

		// Transform phones
		if (phonesToUse.length > 0) {
			await Promise.all(
				phonesToUse.map(async (phone: Phone) => {
					methods.push({
						id: `phone-${phone.id}`,
						type: 'phone',
						value: `${phone.phone}`,
						label: getPhoneTypeLabel(phone.type),
						originalData: phone,
						phoneType: phone.type,
						countryCodeId: phone.countryCode?.id,
						extension: phone.extension,
					})
				})
			)
		}

		contactMethods.value = methods
		emits('update', methods)
	}

	// Watch for data changes and transform to ContactMethod format
	watch(
		[userEmails, usersPhones, loadingEmails, loadingPhones],
		async () => {
			// Solo transformar cuando los datos no estén cargando
			if (!loadingEmails.value && !loadingPhones.value) {
				await transformDataToContactMethods()
			}
		},
		{ immediate: true, deep: true }
	)

	// Forzar recarga de datos cuando el componente se monta
	onMounted(async () => {
		console.log('ContactMethodsTab mounted - refetching data...')
		await Promise.all([refetchEmails(), refetchPhones()])
		// Forzar transformación después del refetch
		await transformDataToContactMethods()
		console.log('Data refetched and transformed:', {
			emails: userEmails.value?.length || 0,
			phones: usersPhones.value?.length || 0,
			contactMethods: contactMethods.value.length,
		})
	})

	const getPhoneTypeLabel = (type: PhoneTypes) => {
		switch (type) {
			case PhoneTypes.Mobile:
				return 'Teléfono Móvil'
			case PhoneTypes.Home:
				return 'Teléfono Casa'
			case PhoneTypes.Oficina:
				return 'Teléfono Trabajo'

			default:
				return 'Teléfono'
		}
	}

	// Función para formatear el número de teléfono
	const formatPhoneNumber = (phone: string) => {
		// Eliminar caracteres no numéricos
		const cleaned = phone.replace(/\D/g, '')

		// Formatear según la longitud
		if (cleaned.length === 10) {
			// Formato: (55) 1234-5678
			return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`
		} else if (cleaned.length === 11) {
			// Formato: (55) 1 1234-5678
			return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 3)} ${cleaned.slice(3, 7)}-${cleaned.slice(7)}`
		} else if (cleaned.length > 6) {
			// Para otros números, agregar guiones
			const lastFour = cleaned.slice(-4)
			const rest = cleaned.slice(0, -4)
			return `${rest}-${lastFour}`
		}

		// Si no coincide con ningún formato, devolver el original
		return phone
	}

	// CRUD Operations
	const addContactMethod = async () => {
		// Activar visualización de errores al intentar enviar
		showFormErrors.value = true

		// Verificar límites antes de validar
		if (newContactMethod.type === 'email' && !canAddEmail.value) {
			toast.error(`Máximo ${MAX_EMAILS} emails permitidos`)
			return
		}
		if (newContactMethod.type === 'phone' && !canAddPhone.value) {
			toast.error(`Máximo ${MAX_PHONES} teléfonos permitidos`)
			return
		}

		try {
			if (newContactMethod.type === 'email') {
				if (!isValidEmail(newContactMethod.value)) {
					toast.error('Por favor ingresa un email válido')
					return
				}

				const data = await createEmailAsync({
					id: props.user.id,
					email: newContactMethod.value,
					type: newContactMethod.emailType,
				})

				if (data) {
					resetNewContactForm()
					showAddForm.value = false
				}
			} else {
				if (!newContactMethod.countryCodeId) {
					toast.error('El código de país es requerido')
					return
				}

				if (!isValidPhone(newContactMethod.value)) {
					toast.error(
						'Por favor ingresa un número de teléfono válido (7-16 dígitos)'
					)

					return
				}

				if (
					newContactMethod.phoneType === PhoneTypes.Oficina &&
					!isValidExtension(newContactMethod.extension || '')
				) {
					toast.error('Por favor ingresa una extensión válida (1-3 dígitos)')
					return
				}

				const data = await createPhoneAsync({
					id: props.user.id,
					phone: newContactMethod.value.replace(/\D/g, ''),
					type: newContactMethod.phoneType,
					countryCodeId: newContactMethod.countryCodeId,
					extension:
						newContactMethod.phoneType === PhoneTypes.Oficina ?
							newContactMethod.extension || ''
						:	'',
				})

				if (data) {
					resetNewContactForm()
					showAddForm.value = false
				}
			}

			await refreshData()
		} catch (error) {
			console.error('Error adding contact method:', error)
		}
	}

	const editContactMethod = (method: ContactMethod) => {
		editingMethod.value = { ...method }

		// Si es un teléfono, establecer el país seleccionado
		if (method.type === 'phone' && method.countryCodeId) {
			const countryCode = countryCodes.value.find(
				(code) => code.id === method.countryCodeId
			)
			if (countryCode) {
				editCountrySelected.value = {
					id: countryCode.id,
					value: countryCode,
				}
			}
		}

		showEditForm.value = true
	}

	const updateContactMethod = async () => {
		if (!editingMethod.value) return

		// Activar visualización de errores al intentar enviar
		showEditErrors.value = true

		try {
			const method = editingMethod.value

			if (method.type === 'email' && method.originalData) {
				if (!isValidEmail(method.value)) {
					toast.error('Por favor ingresa un email válido')
					return
				}

				const data = await updateEmailAsync({
					emailId: (method.originalData as Email).id,
					email: method.value,
					type: method.emailType || EmailTypes.Personal,
				})

				if (data) {
					toast.success('Correo actualizado con éxito')
					editingMethod.value = null
					showEditForm.value = false
				} else {
					showEditForm.value = true
				}
			} else if (method.type === 'phone' && method.originalData) {
				if (!isValidPhone(method.value)) {
					toast.error(
						'Por favor ingresa un número de teléfono válido (7-16 dígitos)'
					)
					return
				}

				if (
					method.phoneType === PhoneTypes.Oficina &&
					!isValidExtension(method.extension || '')
				) {
					toast.error('Por favor ingresa una extensión válida (1-3 dígitos)')
					return
				}

				const data = await updatePhoneAsync({
					phoneId: (method.originalData as Phone).id,
					phone: method.value.replace(/\D/g, ''),
					type: method.phoneType || PhoneTypes.Mobile,
					countryCodeId: method.countryCodeId,
					extension:
						method.phoneType === PhoneTypes.Oficina ?
							method.extension || ''
						:	'',
				})

				if (data) {
					toast.success('Teléfono actualizado con éxito')
					editingMethod.value = null
					showEditForm.value = false
				} else {
					showEditForm.value = true
				}
			}

			await refreshData()
		} catch (error) {
			console.error('Error updating contact method:', error)
		}
	}

	const removeContactMethod = (method: ContactMethod) => {
		methodToDelete.value = method
		deleteConfirm.value = true
	}

	const confirmDelete = async () => {
		if (!methodToDelete.value) return

		try {
			const method = methodToDelete.value

			if (method.type === 'email' && method.originalData) {
				await deleteEmailAsync({
					emailId: (method.originalData as Email).id,
				})
			} else if (method.type === 'phone' && method.originalData) {
				await deletePhoneAsync({
					phoneId: (method.originalData as Phone).id,
				})
			}

			deleteConfirm.value = false
			methodToDelete.value = null

			// Refresh data
			await refreshData()

			toast.success('Método de contacto eliminado exitosamente')
		} catch (error) {
			console.error('Error deleting contact method:', error)
		}
	}

	const refreshData = async () => {
		await Promise.all([refetchEmails(), refetchPhones()])
		emits('refresh')
	}

	const resetNewContactForm = () => {
		newContactMethod.value = ''
		newContactMethod.phoneType = PhoneTypes.Mobile
		newContactMethod.emailType = EmailTypes.Personal
		newContactMethod.countryCodeId = 1
		newContactMethod.extension = ''
		showFormErrors.value = false
		initDefaultCountry()
	}

	const getIcon = (type: string) => {
		switch (type) {
			case 'email':
				return 'mail'
			case 'phone':
				return 'phone'
			default:
				return 'contact_mail'
		}
	}

	// Validación de formato de email
	const isValidEmail = (email: string): boolean => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
		return emailRegex.test(email)
	}

	// Validación de formato de teléfono
	const isValidPhone = (phone: string): boolean => {
		const cleanPhone = phone.replace(/[\s\-()]/g, '')
		return /^\d{7,15}$/.test(cleanPhone)
	}

	// Validación de extensión
	const isValidExtension = (extension: string): boolean => {
		if (!extension || extension.trim() === '') return true // Opcional
		const cleanExt = extension.replace(/[\s-]/g, '')
		return /^\d{1,3}$/.test(cleanExt) // Entre 1 y 6 dígitos
	}

	// Control de cuando mostrar errores
	const showFormErrors = ref(false)
	const showEditErrors = ref(false)

	const { hasPermissionUser } = useHasPermissionUser()
	const USER = 'App\\Models\\User'

	const canViewEmails = computed(() => hasPermissionUser('view-emails', USER))
	const canViewPhones = computed(() => hasPermissionUser('view-phones', USER))
	const canCreateEmails = computed(() => hasPermissionUser('create-emails', USER))
	const canCreatePhones = computed(() => hasPermissionUser('create-phones', USER))
	const canUpdateEmails = computed(() => hasPermissionUser('update-emails', USER))
	const canUpdatePhones = computed(() => hasPermissionUser('update-phones', USER))
	const canDeleteEmails = computed(() => hasPermissionUser('delete-emails', USER))
	const canDeletePhones = computed(() => hasPermissionUser('delete-phones', USER))

	// Límites de métodos de contacto
	const MAX_EMAILS = 3
	const MAX_PHONES = 3

	// Computeds para contar métodos actuales
	const currentEmailCount = computed(() => {
		return contactMethods.value.filter((method) => method.type === 'email')
			.length
	})

	const currentPhoneCount = computed(() => {
		return contactMethods.value.filter((method) => method.type === 'phone')
			.length
	})

	// Computed para verificar si se puede agregar más de cada tipo
	const canAddEmail = computed(() => currentEmailCount.value < MAX_EMAILS && canCreateEmails.value)
	const canAddPhone = computed(() => currentPhoneCount.value < MAX_PHONES && canCreatePhones.value)

	const phoneTypesOptions = computed(() => [
		{ value: PhoneTypes.Mobile, label: 'Móvil' },
		{ value: PhoneTypes.Home, label: 'Casa' },
		{ value: PhoneTypes.Oficina, label: 'Oficina' },
	])

	// Función para cerrar el modal de editar
	const closeEditModal = () => {
		showEditForm.value = false
		editingMethod.value = null
		editCountrySelected.value = undefined
		showEditErrors.value = false // Ocultar errores al cerrar
	}

	// Watchers para ocultar errores cuando el usuario comience a corregir
	watch(
		() => newContactMethod.value,
		() => {
			if (showFormErrors.value && newContactMethod.value) {
				// Verificar si el campo actual es válido para ocultar errores en tiempo real
				const hasValidValue =
					newContactMethod.type === 'email' ?
						isValidEmail(newContactMethod.value)
					:	isValidPhone(newContactMethod.value)
				if (hasValidValue) {
					showFormErrors.value = false
				}
			}
		}
	)

	watch(
		() => newContactMethod.type,
		() => {
			if (showFormErrors.value) {
				showFormErrors.value = false // Ocultar errores al cambiar tipo
			}
		}
	)

	watch(
		() => newContactMethod.type,
		() => {
			if (newContactMethod.type === 'email' && !canAddEmail.value) {
				newContactMethod.type = 'phone'
			} else if (newContactMethod.type === 'phone' && !canAddPhone.value) {
				newContactMethod.type = 'email'
			}
		},
		{ immediate: true }
	)

	const initDefaultCountry = (target: 'add' | 'edit' = 'add') => {
		if (target !== 'add') return
		const mexico = countryCodes.value.find((code) => code.id === 1)
		if (mexico) {
			countrySelected.value = { id: mexico.id, value: mexico }
		}
	}

	watch(showAddForm, (isOpen) => {
		if (isOpen && newContactMethod.type === 'phone') {
			initDefaultCountry()
		}
	})

	const query = ref('')
	const debouncedQuery = ref('')
	let debounceTimer: ReturnType<typeof setTimeout> | null = null

	watch(query, (newQuery) => {
		if (debounceTimer) {
			clearTimeout(debounceTimer)
		}
		debounceTimer = setTimeout(() => {
			debouncedQuery.value = newQuery
		}, 150)
	})

	const filteredCountryCodes = computed(() => {
		if (!debouncedQuery.value || debouncedQuery.value.trim() === '') {
			return countryCodes.value
		}

		const searchTerm = debouncedQuery.value.toLowerCase().trim()

		return countryCodes.value.filter((code) => {
			const codeName = code.name.toLowerCase()
			const codeEnglishName = code.englishName?.toLowerCase() || ''
			const codeIso = code.iso2?.toLowerCase() || code.iso3?.toLowerCase() || ''
			const codeNumber = code.code.toString()

			if (codeName.includes(searchTerm)) return true
			if (codeEnglishName.includes(searchTerm)) return true
			if (codeIso.includes(searchTerm)) return true
			if (codeNumber.includes(searchTerm)) return true
			if (searchTerm.startsWith('+') && codeNumber.includes(searchTerm.substring(1))) return true

			const searchWords = searchTerm.split(' ')
			if (searchWords.every((word) => codeName.includes(word))) return true

			return false
		})
	})

	const countryCodeOptions = computed(() =>
		filteredCountryCodes.value.map((code) => ({
			id: code.id,
			value: code,
			label: `${code.name} (+${code.code})`,
		}))
	)
</script>

<template>
	<div class="w-full max-w-4xl mx-auto p-4">
		<!-- Header -->
		<div class="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-6">
			<div class="flex items-center justify-between">
				<div>
					<h1 class="text-2xl font-bold text-gray-900 mb-2">
						Métodos de contacto ADICIONALES
					</h1>
					<p class="text-gray-600">
						Administra tus emails y teléfonos de contacto
					</p>
				</div>
			</div>
		</div>
		<!-- Loading State -->
		<div
			v-if="isLoading"
			class="bg-white rounded-xl border border-gray-200 p-8"
		>
			<div class="flex items-center justify-center space-x-3">
				<div
					class="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent"
				></div>
				<span class="text-gray-600">Cargando métodos de contacto...</span>
			</div>
		</div>

		<!-- Contact Methods List -->
		<div
			v-else
			class="space-y-2 mb-6"
		>
			<div
				v-for="method in contactMethods"
				v-show="method.type === 'email' ? canViewEmails : canViewPhones"
				:key="method.id"
				class="bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-all duration-200 hover:shadow-md"
			>
				<div class="flex items-center justify-between">
					<!-- Method Info -->
					<div class="flex items-center space-x-3 flex-1">
						<div class="flex-shrink-0">
							<div
								:class="[
									'w-10 h-10 rounded-lg flex items-center justify-center',
									method.type === 'email' ? 'bg-purple-100' : 'bg-green-100',
								]"
							>
								<span
									class="material-symbols-outlined text-base"
									:class="
										method.type === 'email' ?
											'text-purple-600'
										:	'text-green-600'
									"
								>
									{{ getIcon(method.type) }}
								</span>
							</div>
						</div>

						<div class="flex-1 min-w-0">
							<div class="flex items-center space-x-2 mb-1">
								<h3 class="text-sm font-semibold text-gray-900">
									{{ method.label }}
								</h3>
								<span
									v-if="method.type === 'email'"
									class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700"
								>
									Email
								</span>
								<span
									v-else
									class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700"
								>
									Teléfono
								</span>
							</div>

							<div class="flex items-center space-x-1 flex-wrap">
								<!-- Email Format -->
								<span
									v-if="method.type === 'email'"
									class="text-sm text-gray-900 truncate"
								>
									{{ method.value }}
								</span>

								<!-- Phone Format -->
								<div
									v-else
									class="flex items-center gap-1.5"
								>
									<!-- Country Flag & Code -->
									<span class="inline-flex items-center gap-1 text-sm">
										<span class="text-base">{{
											method.countryCodeId ?
												countryCodes.find(
													(code) => code.id === method.countryCodeId
												)?.emoji
											:	'🌐'
										}}</span>
										<span class="text-gray-600 font-medium">
											+{{
												(method.originalData as Phone)?.countryCode?.code ||
												'52'
											}}
										</span>
									</span>

									<!-- Phone Number -->
									<span class="text-gray-900 font-medium">
										{{ formatPhoneNumber(method.value) }}
									</span>

									<!-- Extension -->
									<span
										v-if="method.extension && method.extension.trim() !== ''"
										class="inline-flex items-center px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 text-xs font-medium border border-blue-200"
									>
										Ext. {{ method.extension }}
									</span>
								</div>
							</div>
						</div>
					</div>

					<!-- Actions -->
					<div class="flex items-center space-x-1">
						<button
							v-if="method.type === 'email' ? canUpdateEmails : canUpdatePhones"
							@click="editContactMethod(method)"
							class="inline-flex items-center p-1.5 border border-blue-200 text-xs font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
							title="Editar"
						>
							<span class="material-symbols-outlined text-sm">edit</span>
						</button>

						<button
							v-if="method.type === 'email' ? canDeleteEmails : canDeletePhones"
							@click="removeContactMethod(method)"
							class="inline-flex items-center p-1.5 border border-red-200 text-xs font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 transition-colors"
							title="Eliminar"
						>
							<span class="material-symbols-outlined text-sm">delete</span>
						</button>
					</div>
				</div>
			</div>

			<!-- Empty State -->
			<div
				v-if="contactMethods.length === 0"
				class="bg-white rounded-lg border border-gray-200 p-3 text-center"
			>
				<div
					class="w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6"
				>
					<span class="material-symbols-outlined text-blue-600 text-4xl"
						>contact_mail</span
					>
				</div>
				<h3 class="text-lg font-bold text-gray-900 mb-3">
					Sin métodos de contacto
				</h3>
				<p class="text-gray-600 mb-8 max-w-md mx-auto text-sm">
					Parece que aún no tienes métodos de contacto configurados. ¡Agrega tu
					primer email o teléfono para comenzar!
				</p>
			</div>
		</div>

		<!-- Add New Method -->
		<div
			class="bg-white border border-dashed border-blue-200 rounded-lg p-4 hover:border-blue-300 transition-colors duration-200"
		>
			<div
				v-if="!showAddForm"
				class="text-center"
			>
				<div
					class="w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg flex items-center justify-center mx-auto mb-3"
				>
					<span class="material-symbols-outlined text-blue-600 text-xl"
						>add_circle</span
					>
				</div>
				<h3 class="text-lg font-semibold text-gray-900 mb-2">
					Agregar Método de Contacto
				</h3>
				<p class="text-gray-600 mb-2 text-sm">
					Añade un nuevo email o teléfono
				</p>

				<!-- Mostrar estado de límites -->
				<div class="mb-4 text-xs text-gray-500 space-y-1">
					<div class="flex justify-center items-center space-x-4">
						<span
							:class="
								currentEmailCount >= MAX_EMAILS ?
									'text-red-600 font-medium'
								:	'text-gray-500'
							"
						>
							📧 Emails: {{ currentEmailCount }}/{{ MAX_EMAILS }}
						</span>
						<span
							:class="
								currentPhoneCount >= MAX_PHONES ?
									'text-red-600 font-medium'
								:	'text-gray-500'
							"
						>
							📞 Teléfonos: {{ currentPhoneCount }}/{{ MAX_PHONES }}
						</span>
					</div>
				</div>

				<StyledButton
					v-if="canAddEmail || canAddPhone"
					label="Agregar Método"
					@click="showAddForm = true"
					class="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 text-sm font-semibold shadow-md hover:shadow-lg"
				/>

				<div
					v-else
					class="text-center p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
				>
					<span class="material-symbols-outlined text-yellow-600 mb-2 block"
						>info</span
					>
					<p class="text-sm text-yellow-800 font-medium">Límite alcanzado</p>
					<p class="text-xs text-yellow-700">
						Máximo {{ MAX_EMAILS }} emails y {{ MAX_PHONES }} teléfonos
						permitidos
					</p>
				</div>
			</div>

			<div
				v-else
				class="max-w-lg mx-auto"
			>
				<h3 class="text-lg font-semibold text-gray-900 mb-4 text-center">
					Nuevo Método de Contacto
				</h3>

				<div class="space-y-4">
					<!-- Type Selection -->
					<div>
						<label class="block text-sm font-medium text-gray-700 mb-2">
							Tipo de contacto
						</label>
						<div class="grid grid-cols-2 gap-3">
							<label
								v-if="canCreateEmails"
								class="cursor-pointer"
								:class="!canAddEmail ? 'opacity-50 cursor-not-allowed' : ''"
							>
								<input
									type="radio"
									v-model="newContactMethod.type"
									value="email"
									class="sr-only"
									:disabled="!canAddEmail"
									@click="
										() => {
											newContactMethod.value = ''
										}
									"
								/>
								<div
									class="border-2 rounded-lg p-3 text-center transition-all duration-200"
									:class="[
										newContactMethod.type === 'email' ?
											'border-blue-500 bg-blue-50 text-blue-700'
										:	'border-gray-300 hover:border-blue-300 hover:bg-blue-50',
										!canAddEmail ?
											'border-gray-200 bg-gray-50 text-gray-400'
										:	'',
									]"
								>
									<span class="material-symbols-outlined text-2xl mb-1 block"
										>mail</span
									>
									<span class="text-sm font-semibold">Email</span>
									<div class="text-xs mt-1">
										{{ currentEmailCount }}/{{ MAX_EMAILS }}
										<span
											v-if="!canAddEmail"
											class="text-red-500 ml-1"
											>(Límite)</span
										>
									</div>
								</div>
							</label>

							<label
								v-if="canCreatePhones"
								class="cursor-pointer"
								:class="!canAddPhone ? 'opacity-50 cursor-not-allowed' : ''"
							>
								<input
									type="radio"
									v-model="newContactMethod.type"
									value="phone"
									class="sr-only"
									:disabled="!canAddPhone"
									@click="
										() => {
											newContactMethod.value = ''
										}
									"
								/>
								<div
									class="border-2 rounded-lg p-3 text-center transition-all duration-200"
									:class="[
										newContactMethod.type === 'phone' ?
											'border-green-500 bg-green-50 text-green-700'
										:	'border-gray-300 hover:border-green-300 hover:bg-green-50',
										!canAddPhone ?
											'border-gray-200 bg-gray-50 text-gray-400'
										:	'',
									]"
								>
									<span class="material-symbols-outlined text-2xl mb-1 block"
										>phone</span
									>
									<span class="text-sm font-semibold">Teléfono</span>
									<div class="text-xs mt-1">
										{{ currentPhoneCount }}/{{ MAX_PHONES }}
										<span
											v-if="!canAddPhone"
											class="text-red-500 ml-1"
											>(Límite)</span
										>
									</div>
								</div>
							</label>
						</div>
					</div>

					<!-- Additional Type Selection for Phone -->
					<div v-if="newContactMethod.type === 'phone'">
						<label class="block text-sm font-medium text-gray-700 mb-2">
							Tipo de teléfono
						</label>
						<StyledSelect
							v-model="newContactMethod.phoneType"
							:options="phoneTypesOptions"
							placeholder="Selecciona el tipo de teléfono"
						/>
					</div>

					<!-- Country Code for Phone -->
					<div v-if="newContactMethod.type === 'phone'">
						<StyledCombobox
							v-model="countrySelected"
							:options="countryCodeOptions"
							:prevent-auto-focus="false"
							:prevent-scroll-on-focus="false"
							@input-change="
								(e: string) => {
									query = e
								}
							"
							@model-change="
								(e) => {
									newContactMethod.countryCodeId = e?.id!
								}
							"
							label="Código de país"
							placeholder="Busca por nombre o código (ej: Mexico, MEX, +52, 52)"
							:show-input="!countrySelected"
						>
							<template #default="{ option }">
								<div class="flex items-center gap-2">
									<span class="text-lg">{{ option.value.emoji }}</span>
									<span>{{ option.value.name }}</span>
									<span>(+{{ option.value.code }})</span>
								</div>
							</template>

							<template #input-overlap>
								<div class="flex items-center justify-between gap-2 w-full">
									<span>
										{{ countrySelected?.value.emoji }}
										{{ countrySelected?.value.name }} (+{{
											countrySelected?.value.code
										}})
									</span>

									<div
										@click="
											() => {
												countrySelected = undefined
												query = ''
												newContactMethod.countryCodeId = undefined
											}
										"
										class="cursor-pointer"
									>
										X
									</div>
								</div>
							</template>
						</StyledCombobox>
					</div>

					<StyledInput
						v-model="newContactMethod.value"
						:label="
							newContactMethod.type === 'email' ?
								'Dirección de Email'
							:	'Número de Teléfono'
						"
						:placeholder="
							newContactMethod.type === 'email' ?
								'correo@ejemplo.com'
							:	'55 1234 5678'
						"
						:type="newContactMethod.type === 'email' ? 'email' : 'tel'"
						:max-length="newContactMethod.type === 'email' ? 100 : 16"
						:is-required="true"
					/>

					<!-- Extension for Office Phone -->
					<div
						v-if="
							newContactMethod.type === 'phone' &&
							newContactMethod.phoneType === PhoneTypes.Oficina
						"
					>
						<StyledInput
							v-model="newContactMethod.extension"
							label="Extensión"
							placeholder="ej. 101"
							type="text"
							:is-required="true"
						/>
					</div>

					<!-- Actions -->
					<div class="flex space-x-3 pt-2">
						<StyledButton
							label="Cancelar"
							@click="
								() => {
									showAddForm = false
									resetNewContactForm()
								}
							"
							class="flex-1 border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 text-sm"
						/>
						<StyledButton
							label="Agregar"
							@click="addContactMethod"
							class="flex-1 bg-blue-600 text-white text-sm"
						/>
					</div>
				</div>
			</div>
		</div>

		<!-- Edit Modal -->
		<div
			v-if="showEditForm && editingMethod"
			class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
		>
			<div class="bg-white rounded-lg p-4 w-full max-w-md mx-4">
				<h3 class="text-lg font-semibold text-gray-900 mb-3">
					Editar Método de Contacto
				</h3>

				<div class="space-y-3">
					<StyledInput
						v-if="editingMethod.type !== 'phone'"
						v-model="editingMethod.value"
						:label="
							editingMethod.type === 'email' ?
								'Dirección de email'
							:	'Número de teléfono'
						"
						:type="editingMethod.type === 'email' ? 'email' : 'tel'"
						:max-length="editingMethod.type === 'email' ? 100 : 16"
						:is-required="true"
					/>

					<StyledInput
						v-else
						v-model="editingMethod.value"
						label="Número de teléfono"
						type="number"
						:max-length="16"
						is-required
					/>

					<!-- Type selection for phones -->
					<div v-if="editingMethod.type === 'phone'">
						<StyledSelect
							v-model="editingMethod.phoneType"
							:options="phoneTypesOptions"
							label="Tipo de teléfono"
						/>
					</div>

					<!-- Country code for phones -->
					<div v-if="editingMethod.type === 'phone'">
						<StyledCombobox
							v-model="editCountrySelected"
							:options="countryCodeOptions"
							:prevent-auto-focus="false"
							:prevent-scroll-on-focus="false"
							@input-change="
								(e: string) => {
									query = e
								}
							"
							@model-change="
								(e) => {
									if (editingMethod) {
										editingMethod.countryCodeId = e?.id!
									}
								}
							"
							label="Código de país"
							placeholder="Busca por nombre o código (ej: México, +52, 52)"
							:show-input="!editCountrySelected"
						>
							<template #default="{ option }">
								<div class="flex items-center gap-2">
									<span class="text-lg">{{ option.value.emoji }}</span>
									<span>{{ option.value.name }}</span>
									<span>(+{{ option.value.code }})</span>
								</div>
							</template>

							<template #input-overlap>
								<div class="flex items-center justify-between gap-2 w-full">
									<span>
										{{ editCountrySelected?.value.emoji }}
										{{ editCountrySelected?.value.name }} (+{{
											editCountrySelected?.value.code
										}})
									</span>

									<div
										@click="
											() => {
												editCountrySelected = undefined
												query = ''
												if (editingMethod) {
													editingMethod.countryCodeId = undefined
												}
											}
										"
										class="cursor-pointer"
									>
										X
									</div>
								</div>
							</template>
						</StyledCombobox>
					</div>

					<!-- Extension for Office Phone -->
					<div
						v-if="
							editingMethod.type === 'phone' &&
							editingMethod.phoneType === PhoneTypes.Oficina
						"
					>
						<StyledInput
							v-model="editingMethod.extension"
							label="Extensión"
							placeholder="ej. 101"
							type="text"
						/>
					</div>

					<div class="flex space-x-4 pt-4">
						<StyledButton
							label="Cancelar"
							@click="closeEditModal"
							class="flex-1 border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
						/>
						<StyledButton
							label="Guardar"
							@click="updateContactMethod"
							class="flex-1 bg-blue-600 text-white"
						/>
					</div>
				</div>
			</div>
		</div>

		<!-- Delete Confirmation Modal -->
		<AlertComponent
			v-model="deleteConfirm"
			type="warning"
			:dismiss="true"
			:on-confirm="confirmDelete"
		>
			<template #title> ¿Eliminar método de contacto? </template>

			<template #body>
				Esta acción no se puede deshacer. El método de contacto será eliminado
				permanentemente.
			</template>

			<template #closeButton> Cancelar </template>

			<template #button> Eliminar </template>
		</AlertComponent>

		<!-- Loading Modal -->
		<LoadingModal :when="isLoading" />
	</div>
</template>

<style scoped>
	.fade-transition {
		transition: all 0.3s ease;
	}

	.material-symbols-outlined {
		font-variation-settings:
			'FILL' 0,
			'wght' 400,
			'GRAD' 0,
			'opsz' 24;
	}

	/* Custom border width utility */
	.border-3 {
		border-width: 3px;
	}

	/* Enhanced hover animations */
	.group:hover .group-hover\:bg-blue-200 {
		background-color: rgb(191 219 254);
	}

	.group:hover .group-hover\:bg-green-200 {
		background-color: rgb(187 247 208);
	}

	/* Enhanced gradient backgrounds */
	.bg-gradient-to-r {
		background-image: linear-gradient(to right, var(--tw-gradient-stops));
	}

	.bg-gradient-to-br {
		background-image: linear-gradient(
			to bottom right,
			var(--tw-gradient-stops)
		);
	}

	/* Custom shadow utilities */
	.shadow-xl {
		box-shadow:
			0 20px 25px -5px rgb(0 0 0 / 0.1),
			0 8px 10px -6px rgb(0 0 0 / 0.1);
	}

	/* Enhanced loading animation */
	@keyframes spin {
		from {
			transform: rotate(0deg);
		}
		to {
			transform: rotate(360deg);
		}
	}

	.animate-spin {
		animation: spin 1s linear infinite;
	}
</style>
